import {
  clearSafeTimeout,
  scheduleSafeTimeout,
} from '../../dashboard/helper/browserTimers';

export const debounce = (callback, wait, immediate = false) => {
  let timeout;

  function debouncedCallback(...args) {
    const callNow = immediate && !timeout;
    const later = () => {
      timeout = null;
      if (!immediate) callback.apply(null, args);
    };

    clearSafeTimeout(timeout);
    timeout = scheduleSafeTimeout(later, wait);
    if (callNow) callback.apply(null, args);
  }

  debouncedCallback.cancel = () => {
    clearSafeTimeout(timeout);
    timeout = null;
  };

  return debouncedCallback;
};

export const getContrastingTextColor = (bgColor) => {
  const color = bgColor.replace('#', '');
  const red = parseInt(color.slice(0, 2), 16);
  const green = parseInt(color.slice(2, 4), 16);
  const blue = parseInt(color.slice(4, 6), 16);

  return red * 0.299 + green * 0.587 + blue * 0.114 > 186
    ? '#000000'
    : '#FFFFFF';
};

export const parseBoolean = (candidate) => {
  try {
    return Boolean(JSON.parse(String(candidate).toLowerCase()));
  } catch {
    return false;
  }
};

const MESSAGE_VARIABLES_REGEX = /{{(.*?)}}/g;

const skipCodeBlocks = (content) => content.replace(/```(?:.|\n)+?```/g, '');

const capitalizeName = (name) => {
  return (name || '').replace(/\b(\w)/g, (letter) => letter.toUpperCase());
};

const getFirstName = ({ user }) => {
  const firstName = user?.name ? user.name.split(' ').shift() : '';
  return capitalizeName(firstName);
};

const getLastName = ({ user }) => {
  if (!user?.name) return '';

  const nameParts = user.name.split(' ');
  const lastName = nameParts.length > 1 ? nameParts.pop() : '';
  return capitalizeName(lastName);
};

export const getMessageVariables = ({ conversation, contact }) => {
  const {
    meta: { assignee, sender },
    id,
    custom_attributes: conversationCustomAttributes = {},
  } = conversation;
  const { custom_attributes: contactCustomAttributes } = contact || {};

  const conversationCustomAttributeVariables = Object.entries(
    conversationCustomAttributes || {}
  ).reduce((accumulator, [key, value]) => {
    accumulator[`conversation.custom_attribute.${key}`] = value;
    return accumulator;
  }, {});

  const contactCustomAttributeVariables = Object.entries(
    contactCustomAttributes || {}
  ).reduce((accumulator, [key, value]) => {
    accumulator[`contact.custom_attribute.${key}`] = value;
    return accumulator;
  }, {});

  return {
    'contact.name': capitalizeName(sender?.name || ''),
    'contact.first_name': getFirstName({ user: sender }),
    'contact.last_name': getLastName({ user: sender }),
    'contact.email': sender?.email,
    'contact.phone': sender?.phone_number,
    'contact.id': sender?.id,
    'conversation.id': id,
    'agent.name': capitalizeName(assignee?.name || ''),
    'agent.first_name': getFirstName({ user: assignee }),
    'agent.last_name': getLastName({ user: assignee }),
    'agent.email': assignee?.email ?? '',
    ...conversationCustomAttributeVariables,
    ...contactCustomAttributeVariables,
  };
};

export const replaceVariablesInMessage = ({ message, variables }) => {
  return message?.replace(MESSAGE_VARIABLES_REGEX, (_, replacement) => {
    const key = replacement.trim();
    return variables[key] ? variables[key.toLowerCase()] : '';
  });
};

export const getUndefinedVariablesInMessage = ({ message, variables }) => {
  const matches = skipCodeBlocks(message).match(MESSAGE_VARIABLES_REGEX);
  if (!matches) return [];

  return matches
    .map((match) => match.replace('{{', '').replace('}}', '').trim())
    .filter((variable) => variables[variable] === undefined);
};

export const createTypingIndicator = (
  onStartTyping,
  onStopTyping,
  idleTime
) => {
  let timer = null;

  const stop = () => {
    if (timer) {
      clearSafeTimeout(timer);
      timer = null;
      onStopTyping();
    }
  };

  const reset = () => {
    if (timer) clearSafeTimeout(timer);
    timer = scheduleSafeTimeout(stop, idleTime);
  };

  const start = () => {
    if (!timer) onStartTyping();
    reset();
  };

  return { start, stop };
};

export const formatTime = (timeInSeconds) => {
  if (timeInSeconds >= 86400) {
    const days = Math.floor(timeInSeconds / 86400);
    const hours =
      timeInSeconds % 86400 < 3600 || days >= 364
        ? 0
        : Math.floor((timeInSeconds % 86400) / 3600);
    return `${days} Day${hours > 0 ? ` ${hours} Hr` : ''}`;
  }

  if (timeInSeconds >= 3600) {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes =
      timeInSeconds % 3600 < 60 || hours === 24
        ? 0
        : Math.floor((timeInSeconds % 3600) / 60);
    return `${hours} Hr${minutes > 0 ? ` ${minutes} Min` : ''}`;
  }

  if (timeInSeconds >= 60) {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = minutes === 60 ? 0 : Math.floor(timeInSeconds % 60);
    return `${minutes} Min${seconds > 0 ? ` ${seconds} Sec` : ''}`;
  }

  return `${Math.floor(timeInSeconds)} Sec`;
};

export const convertSecondsToTimeUnit = (seconds, unitNames) => {
  if (seconds === null || seconds === 0) return { time: '', unit: '' };
  if (seconds < 3600) {
    return { time: Number((seconds / 60).toFixed(1)), unit: unitNames.minute };
  }
  if (seconds < 86400) {
    return { time: Number((seconds / 3600).toFixed(1)), unit: unitNames.hour };
  }
  return { time: Number((seconds / 86400).toFixed(1)), unit: unitNames.day };
};

const sortAsc = (values) => values.slice().sort((left, right) => left - right);

const clamp = (min, max, value) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const quantileForSorted = (sorted, quantile) => {
  const clamped = clamp(0, 1, quantile);
  const position = (sorted.length - 1) * clamped;
  const base = Math.floor(position);
  const rest = position - base;

  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }

  return sorted[base];
};

export const getQuantileIntervals = (data, intervals) => {
  const sorted = sortAsc(data);
  return intervals.map((interval) => quantileForSorted(sorted, interval));
};
